import React from 'react';
import { Layers, ChevronRight, Trash2 } from 'lucide-react';
import { truncatePromptForTooltip } from '@/shared/utils/historyFormatting';
import type { WorkoutHistoryItem } from '@/features/workout-history';
import type { TranslationKeys } from '@/core/i18n/translations';

type Lang = 'en' | 'pl';

export interface WorkoutHistoryPanelProps {
  t: TranslationKeys;
  language: Lang;
  session: unknown;
  fetchingWorkouts: boolean;
  workouts: WorkoutHistoryItem[];
  onSelectWorkout: (w: WorkoutHistoryItem) => void;
  onDeleteWorkout: (id: string) => void | Promise<void>;
  deletingWorkoutId: string | null;
  onOpenLogin?: () => void;
}

export function WorkoutHistoryPanel({
  t,
  language,
  session,
  fetchingWorkouts,
  workouts,
  onSelectWorkout,
  onDeleteWorkout,
  deletingWorkoutId,
  onOpenLogin,
}: WorkoutHistoryPanelProps) {
  const grouped = workouts.reduce<Record<string, WorkoutHistoryItem[]>>((acc, workout) => {
    const dateKey = new Date(workout.created_at).toISOString().slice(0, 10);
    if (!acc[dateKey]) acc[dateKey] = [];
    acc[dateKey].push(workout);
    return acc;
  }, {});

  const dateKeys = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

  return session ? (
    <section className="bg-white p-6 rounded-2xl border border-blue-200 shadow-xl backdrop-filter backdrop-blur-lg bg-opacity-80 dark:bg-slate-900 dark:border-slate-700 dark:bg-opacity-10">
      <h2 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2">
        <Layers className="w-5 h-5 text-purple-500" />
        {t.historyTab}
      </h2>
      {fetchingWorkouts ? (
        <p>Ładowanie historii treningów...</p>
      ) : workouts.length > 0 ? (
        <div className="space-y-4">
          {dateKeys.map((dateKey) => {
            const dayDate = new Date(`${dateKey}T00:00:00`);
            const dayLabel = dayDate.toLocaleDateString(language === 'pl' ? 'pl-PL' : undefined, {
              weekday: 'short',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            });
            return (
              <div key={dateKey} className="rounded-xl border border-slate-200 dark:border-slate-700 p-3">
                <h3 className="mb-3 text-sm font-semibold text-slate-600 dark:text-slate-300">{dayLabel}</h3>
                <ul className="grid grid-cols-1 xl:grid-cols-2 gap-2">
                  {grouped[dateKey].map((workout) => {
                    const tipRaw = truncatePromptForTooltip(workout.prompt ?? '');
                    const tip = tipRaw || t.historyPromptTooltipEmpty;
                    const isDeleting = deletingWorkoutId === workout.id;
                    return (
                      <li key={workout.id} className="flex gap-2 items-stretch">
                        <button
                          type="button"
                          data-testid={`history-workout-${workout.id}`}
                          title={tip}
                          onClick={() => onSelectWorkout(workout)}
                          className="group flex-1 min-w-0 text-left min-h-[44px] p-3 bg-slate-50 dark:bg-slate-800 rounded-lg flex flex-row items-center justify-between gap-3 hover:bg-blue-50/60 dark:hover:bg-slate-700/60 cursor-pointer transition-colors border border-transparent hover:border-blue-200 dark:hover:border-slate-600 touch-manipulation"
                        >
                          <span className="flex min-w-0 flex-col gap-1">
                            <span className="font-medium truncate">{workout.name}</span>
                            <span className="text-xs text-slate-500 dark:text-slate-400">
                              {new Date(workout.created_at).toLocaleDateString(
                                language === 'pl' ? 'pl-PL' : undefined,
                              )}
                            </span>
                          </span>
                          <ChevronRight
                            className="w-4 h-4 shrink-0 text-slate-300 dark:text-slate-600 transition-all group-hover:text-blue-500 group-hover:translate-x-0.5"
                            aria-hidden="true"
                          />
                        </button>
                        <button
                          type="button"
                          data-testid={`history-workout-delete-${workout.id}`}
                          aria-label={language === 'pl' ? 'Usuń trening z historii' : 'Remove workout from history'}
                          disabled={isDeleting}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!window.confirm('Czy na pewno chcesz usunąć ten trening?')) return;
                            void onDeleteWorkout(workout.id);
                          }}
                          className="shrink-0 self-stretch px-3 rounded-lg border border-transparent bg-slate-50 dark:bg-slate-800 text-gray-400 hover:text-red-500 hover:border-red-200 dark:hover:border-red-900/60 transition-colors touch-manipulation disabled:opacity-50 disabled:pointer-events-none"
                        >
                          <Trash2 className="w-4 h-4" aria-hidden="true" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-slate-500">Brak zapisanych treningów.</p>
      )}
    </section>
  ) : (
    <section className="bg-white p-6 rounded-2xl border border-blue-200 shadow-xl backdrop-filter backdrop-blur-lg bg-opacity-80 dark:bg-slate-900 dark:border-slate-700 dark:bg-opacity-10">
      <h2 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2">
        <Layers className="w-5 h-5 text-purple-500" />
        {t.historyTab}
      </h2>
      <div className="rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-900/80 text-slate-900 dark:text-slate-100 p-5 text-center text-base leading-relaxed shadow-sm">
        <button
          type="button"
          onClick={() => onOpenLogin?.()}
          className="font-semibold text-orange-700 dark:text-orange-300 underline underline-offset-2"
        >
          {t.loginToSaveWorkoutsLoginCta}
        </button>
        {t.loginToSaveWorkoutsSuffix}
      </div>
    </section>
  );
}
